// ブラウザ完結版バックエンド。Node 版 server.mjs の /api/* 契約を、既存 lib/*.mjs をそのまま流用して
// ブラウザ内で再現する。VFS は単一シングルトンのため、全 mutating op を async mutex で直列化し、
// op ごとに「ハイドレート → 実行 → flush（prefix で 2 ストアへ振り分け＋削除反映）」を行う。
import path from './shims/path.mjs';
import { vfs } from './vfs.mjs';
import { ensureSeeded, ensureRuntimeDefaults, ensureRuntimeMap, RUNTIME_SHARED } from './seed.mjs';
import {
    STORE_PROJECT_FILES, STORE_PREVIEW_FILES,
    listProjectMetas, putProjectMeta, deleteProjectMeta,
    getAllFiles, getFile, applyFileChanges, deleteAllFiles,
    getAssetFile, hasAssetFile, applyAssetChanges, deleteAssetsForProject, getAssetsForProject,
    getProjectMeta, listFileSizes, listAssetSizes,
} from './idb.mjs';
import { createZip, readZip } from './zip.mjs';
import {
    ArchiveError, MANIFEST_NAME, buildArchive, archiveFileName, parseManifest, summarizeArchive,
    planImport, rewriteEntryForPlan, buildImportedMeta, entryName, suggestCloneGameId,
} from './project-archive.mjs';
import { __setGameRegistry } from './shims/games.mjs';
import { migrateEnemyGlbAssets } from './glb-migrate.mjs';
import { rewriteEnemyModelPaths, rewriteEnemyPromptIndex } from '../lib/glb-migrate-core.mjs';

// 流用する既存 lib（fs/path/... は esbuild プラグインで shim へ差し替わる）
import {
    createProject, listProjects, getProjectDetail, readProjectMeta, updateProjectMeta,
    deleteProject, saveAnalysis, readAnalysis, readNovel, readImagePromptFile, saveRequestText, projectPaths,
    STORY_STEPS, invalidatePipelineFrom, replaceProjectNovel, recordStep,
} from '../lib/story-project.mjs';
import { mergeEditableGameSettings, runAnalyze, normalizeAnalysis } from '../lib/story-analyze.mjs';
import { runScenarioDraft, scenarioUiLimits, estimateScenarioCost } from '../lib/story-scenario.mjs';
import { inspectNovelSource, resolveDocumentOriginalUrl } from '../lib/story-source.mjs';
import { runWorldDesign, saveWorldDesign, WORLD_DESIGN_FORMAT_VERSION } from '../lib/story-world-design.mjs';
import { runImagePrompts } from '../lib/story-image-prompts.mjs';
import { runImageGenerate, saveUploadedImage } from '../lib/story-image-gen.mjs';
import {
    runStartScreenGenerate, saveStartScreenUpload, saveStartScreenSettings,
    readStartScreenState, applyStartScreenToGame, refineStartScreenPortrait, START_SCREEN_JOB_KIND,
} from '../lib/story-start-screen.mjs';
import { runModelGenerate, saveUploadedGlb, reapplyPlayableGlbPaths } from '../lib/story-model-gen.mjs';
import {
    buildPublishPackage, contentByteLength, buildPublishRuntimeConfig, buildMinimalCacheManifest,
    collectPublishFiles, toPackageEntries,
} from './publish-package.mjs';
import {
    buildScaffoldOptions, linkGame, buildRequestText, runGenerateJson,
    listGenerateUnits, buildImportPlan, runImport, readGeneratedUnitJson, saveGeneratedUnitJson,
    applyGeneratedEnemyMotionProfiles, reapplyWorldDesignArtifacts, WORLD_ARTIFACTS_VERSION,
    applyEditedGameSettings,
} from '../lib/story-generate.mjs';
import { importFromText } from '../lib/import-json.mjs';
import { buildAiRequest } from '../lib/prompt-builder.mjs';
import {
    effectiveImageModelCatalog, llmStatus, llmHealth, llmConfig,
    openRouterModelPricing, openRouterModelSettings, resolveTaskModel,
} from '../lib/llm-client.mjs';
import { formatOpenRouterJobCostLine, summarizeJobCost } from '../lib/generation-cost.mjs';
import { meshyConfig, resolveMeshyKey } from '../lib/meshy-client.mjs';
import { uiConfig } from '../lib/ui-config.mjs';
import { readJson, readJsonIfExists, writeJson } from '../lib/json-utils.mjs';
import { readGenerationLogs, saveGenerationLog } from '../lib/generation-log-store.mjs';
import { runValidateMapsInProcess } from '../lib/validate-schema.mjs';
import { runIntegrityCheck } from '../lib/validate-integrity.mjs';
import { runI18nCheck, formatI18nFindings } from '../lib/validate-i18n.mjs';
import { collectAssetRefs } from '../lib/validate-assets.mjs';
import { generateTreasuresInVfs } from './gen/treasure-generator.mjs';
import {
    buildCharactersJson, buildEmptyTreasuresJson, patchSkillsCharacterSkills, patchCharacterLocale,
} from '../lib/cast-package.mjs';

const PROJECTS_ROOT = '/vfs/tools/projects';
const NEWGAMES_ROOT = '/vfs/newgames';
const CONFIG_DIR = '/vfs/tools/config';
const ASSETS_ROOT = '/vfs/assets'; // Meshy 生成 GLB 等（meshy-client downloadToAssets の書き込み先）

const projectDir = (pid) => `${PROJECTS_ROOT}/${pid}`;
const gameDir = (gid) => `${NEWGAMES_ROOT}/${gid}`;

function parseStoredJson(data) {
    if (data === undefined || data === null) return null;
    try {
        if (typeof data === 'string') return JSON.parse(data);
        const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
}

/** ブラウザ保存済みの物語プロジェクトを、ローカル版listGamesと同じUI用の形へまとめる。 */
async function listBrowserGames() {
    const metas = await listProjectMetas();
    const games = await Promise.all(metas.filter((meta) => meta.gameId).map(async (meta) => {
        const [analysisData, mapListData] = await Promise.all([
            getFile(STORE_PROJECT_FILES, meta.projectId, 'analysis.json'),
            getFile(STORE_PREVIEW_FILES, meta.projectId, 'maps/_shared/map-list.json'),
        ]);
        const analysis = parseStoredJson(analysisData);
        const mapList = parseStoredJson(mapListData);
        return {
            gameId: meta.gameId,
            title: analysis?.game?.title ?? meta.title ?? null,
            createdAt: meta.createdAt ?? null,
            startMapId: analysis?.game?.startMapId ?? null,
            characters: analysis?.game?.characters ?? null,
            published: false,
            storyProjectId: meta.projectId,
            maps: Array.isArray(mapList?.maps)
                ? mapList.maps
                : (analysis?.game?.startMapId ? [analysis.game.startMapId] : []),
            version: null,
            lastValidation: null,
            lastOperations: null,
            manualChecks: {},
            pendingConstants: [],
        };
    }));
    return games.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

/** ブラウザ版アセット検証: 生成GLBはIndexedDB、共有アセットは同一オリジン/R2配信を確認する。 */
async function runBrowserAssetCheck(gameId, { onLine = () => {}, signal = null } = {}) {
    const refs = collectAssetRefs(gameId);
    const queue = [...refs.entries()];
    const present = [];
    const missing = [];
    const assetUrl = (rel) => `/assets/${rel.split('/').map(encodeURIComponent).join('/')}`;
    const workers = Array.from({ length: Math.min(5, Math.max(1, queue.length)) }, async () => {
        while (queue.length > 0) {
            signal?.throwIfAborted();
            const [asset, usages] = queue.shift() ?? [];
            if (!asset) break;
            let ok = await hasAssetFile(asset);
            if (!ok) {
                try {
                    const timeoutSignal = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
                        ? AbortSignal.timeout(10000) : null;
                    const requestSignal = signal && timeoutSignal && typeof AbortSignal.any === 'function'
                        ? AbortSignal.any([signal, timeoutSignal])
                        : (signal ?? timeoutSignal ?? undefined);
                    const response = await fetch(assetUrl(asset), { method: 'HEAD', cache: 'no-store', signal: requestSignal });
                    ok = response.ok;
                } catch (e) {
                    if (signal?.aborted) throw e;
                    ok = false;
                }
            }
            (ok ? present : missing).push({ asset, usages });
        }
    });
    await Promise.all(workers);
    const result = {
        ok: missing.length === 0,
        totalRefs: refs.size,
        presentCount: present.length,
        missing: missing.map((entry) => ({ ...entry, usages: entry.usages.slice(0, 5), usageCount: entry.usages.length })),
        browserChecked: true,
    };
    onLine(`assets: ${result.ok ? 'OK' : 'NG'}（確認 ${refs.size} / 不足 ${missing.length}）`);
    for (const entry of result.missing) onLine(`  ✖ /assets/${entry.asset}`);
    return result;
}

// ── async mutex（全 mutating op を直列化）─────────────────
// タブ内の直列化（queue）に加えて、Web Locks で同一オリジンの全タブ間でも直列化する。
// IndexedDB は全タブ共有のため、タブ間で hydrate→flush が交差すると lost update や
// 削除済みプロジェクトの部分復活が起きる（Web Locks 非対応環境ではタブ内直列化のみ）。
let queue = Promise.resolve();
function runExclusive(fn) {
    const locked = (typeof navigator !== 'undefined' && navigator.locks?.request)
        ? () => navigator.locks.request('vn_newgames_tool_op', () => fn())
        : fn;
    const run = queue.then(locked, locked);
    // 失敗しても次の op を止めない
    queue = run.then(() => {}, () => {});
    return run;
}

// ── ハイドレート ─────────────────────────────────
async function hydrateRegistry() {
    const metas = await listProjectMetas();
    __setGameRegistry(metas.filter((m) => m.gameId).map((m) => ({ gameId: m.gameId })));
}

async function hydrateProject(projectId) {
    vfs.mkdir(projectDir(projectId));
    for (const { path: rel, data } of await getAllFiles(STORE_PROJECT_FILES, projectId)) {
        vfs.hydrate(`${projectDir(projectId)}/${rel}`, data);
    }
}

/**
 * 解析コストの予測（📖 ゲームシナリオ文書作成の確認ダイアログ用）。
 * 単価は OpenRouter の原値を都度取得する。取れない場合もトークン数だけは返し、
 * 「予測が出せない」ことを理由つきで画面へ伝える（実行自体は止めない）。
 */
async function scenarioCostPreview(text) {
    let pricing = null;
    let pricingError = null;
    try {
        pricing = (await openRouterModelPricing(resolveTaskModel('scenario'))).pricing;
    } catch (e) {
        pricingError = e?.message ?? String(e);
    }
    return { estimate: estimateScenarioCost(text, pricing), pricingError };
}

function hydrateGameDefaults(gid) {
    const dir = gameDir(gid);
    vfs.hydrate(`${dir}/package.json`, `{"name":"${gid}","private":true,"version":"0.0.0"}\n`);
    for (const name of vfs.readdir('/vfs/_schemas')) {
        vfs.hydrate(`${dir}/schemas/${name}`, vfs.read(`/vfs/_schemas/${name}`));
    }
    for (const f of RUNTIME_SHARED) {
        const src = `/vfs/_runtime_defaults/maps/_shared/${f}`;
        if (vfs.existsFile(src)) vfs.hydrate(`${dir}/maps/_shared/${f}`, vfs.read(src));
    }
}

async function hydrateGame(gid, projectId) {
    hydrateGameDefaults(gid);            // 既定（schemas / _shared マスタ / dummy package.json）
    for (const { path: rel, data } of await getAllFiles(STORE_PREVIEW_FILES, projectId)) {
        vfs.hydrate(`${gameDir(gid)}/${rel}`, data);   // previewFiles が既定を上書き
    }
    // 2026-07-13以前の生成データでは、人型敵にも任意の *.motion.json 取得が走って404になった。
    // プロンプト索引の humanoid / motionProfile を正として敵マスタを自己修復し、flushで永続化する。
    const enemiesPath = `${gameDir(gid)}/maps/_shared/enemies.json`;
    const enemies = readJsonIfExists(enemiesPath);
    const promptIndex = readJsonIfExists(projectPaths(projectId).imagePromptsIndex);
    if (applyGeneratedEnemyMotionProfiles(enemies, promptIndex) > 0) writeJson(enemiesPath, enemies);
    // 旧2ファイル形式（_btl/_move）の敵GLBは、開いた時点で統合1ファイル形式へ無償・決定的に移行する
    // （2026-08-08 統一。アニメ無しの複製ペアはコピーのみ・失敗ペアは旧形式のまま残す）。
    // 移行できた base だけ敵マスタとプロンプト索引の参照を統合パスへ書き換え、flushで永続化する。
    const glbMigration = await migrateEnemyGlbAssets({ projectId, gameId: gid });
    if (glbMigration.migratedBases.length > 0) {
        if (rewriteEnemyModelPaths(enemies, gid, glbMigration.migratedBases) > 0) writeJson(enemiesPath, enemies);
        if (rewriteEnemyPromptIndex(promptIndex, gid, glbMigration.migratedBases) > 0) {
            writeJson(projectPaths(projectId).imagePromptsIndex, promptIndex);
        }
    }
    // 旧ワールド設計はブラウザでプロジェクトを開いた時点で無償・決定的に移行し、
    // 現在のプレビューへ再反映する。⑤の有料LLM再生成を要求せず、海・湖などの深度改善も既存案件へ届ける。
    const worldDesign = readJsonIfExists(projectPaths(projectId).worldDesign);
    const projectMeta = readProjectMeta(projectId);
    if (worldDesign && (
        worldDesign.formatVersion !== WORLD_DESIGN_FORMAT_VERSION
        || Number(projectMeta.worldArtifactsVersion) !== WORLD_ARTIFACTS_VERSION
    )) {
        reapplyWorldDesignArtifacts(projectId, gid);
    }
}

// ── flush（prefix で振り分け）─────────────────────────
// 書き込み順は previewFiles → projectFiles → projects 索引。トランザクションが分かれているため
// 容量超過などで途中失敗しても「索引（進捗表示）だけ進んで実体が無い」状態を作らない
//（ユーザーに見える状態＝索引を最後に確定する）。
async function flush(projectId, gid) {
    // 書込に成功したキーは markFlushed で差分追跡から外す（ユニット境界の checkpoint で
    // 同じ op 内から複数回呼ばれるため、外さないと過去分を毎回再書き込みして O(N²) になる）
    if (gid) {
        const base = gameDir(gid) + '/';
        const dirtyEntries = vfs.dirtyUnder(gameDir(gid)).filter(([k]) => {
            const p = k.slice(base.length);
            return !p.startsWith('.') && !p.startsWith('schemas/') && p !== 'package.json';
        });
        const unlinkedKeys = vfs.unlinkedUnder(gameDir(gid)).filter((k) => {
            const p = k.slice(base.length);
            return p && !p.startsWith('.');
        });
        const puts = dirtyEntries.map(([k, v]) => ({ path: k.slice(base.length), data: v }));
        const dels = unlinkedKeys.map((k) => k.slice(base.length));
        if (puts.length || dels.length) {
            await applyFileChanges(STORE_PREVIEW_FILES, projectId, puts, dels);
            vfs.markFlushed(dirtyEntries, unlinkedKeys);
        }
    }
    // Meshy 生成 GLB（/vfs/assets/**）→ assetFiles ストア（path 単独キー・projectId は掃除用）。
    // モデル生成以外の op では dirty が空＝コストゼロ
    if (projectId) {
        const base = ASSETS_ROOT + '/';
        const dirtyEntries = vfs.dirtyUnder(ASSETS_ROOT);
        const unlinkedKeys = vfs.unlinkedUnder(ASSETS_ROOT);
        if (dirtyEntries.length || unlinkedKeys.length) {
            await applyAssetChanges(
                projectId,
                dirtyEntries.map(([k, v]) => ({ path: k.slice(base.length), data: v })),
                unlinkedKeys.map((k) => k.slice(base.length)),
            );
            vfs.markFlushed(dirtyEntries, unlinkedKeys);
        }
    }
    if (projectId) {
        const base = projectDir(projectId) + '/';
        const dirtyEntries = vfs.dirtyUnder(projectDir(projectId));
        const unlinkedKeys = vfs.unlinkedUnder(projectDir(projectId)).filter((k) => k.slice(base.length));
        const puts = dirtyEntries.map(([k, v]) => ({ path: k.slice(base.length), data: v }));
        const dels = unlinkedKeys.map((k) => k.slice(base.length));
        if (puts.length || dels.length) {
            await applyFileChanges(STORE_PROJECT_FILES, projectId, puts, dels);
            vfs.markFlushed(dirtyEntries, unlinkedKeys);
        }
        // projects 索引（一覧の高速化）: project.json + analysis タイトルを denormalize。
        // title は Node 版 listProjects と同じ {ja,en} オブジェクト形で持つ（app.js は p.title?.ja で参照）。
        const meta = readJsonIfExists(`${projectDir(projectId)}/project.json`);
        if (meta) {
            const analysis = readJsonIfExists(`${projectDir(projectId)}/analysis.json`);
            await putProjectMeta({ ...meta, projectId, title: analysis?.game?.title ?? null });
        }
    }
}

/** flush の失敗をユーザーが対処できる文言へ変換する。 */
function flushErrorToFriendly(err) {
    if (err?.name === 'QuotaExceededError') {
        return new Error('ブラウザの保存容量が不足しています。ホーム画面から不要な物語プロジェクトを削除してから再実行してください。');
    }
    return err;
}

/** projectId（と meta.gameId）の VFS を用意して fn を実行し、書き込みを flush する。 */
async function withProjectVfs(projectId, fn, { needRuntime = false } = {}) {
    return runExclusive(async () => {
        ensureSeeded();
        vfs.resetLayer();
        await hydrateRegistry();
        let gid = null;
        let fnError = null;
        try {
            if (projectId) {
                await hydrateProject(projectId);
                const meta = readJsonIfExists(`${projectDir(projectId)}/project.json`);
                gid = meta?.gameId ?? null;
            }
            if (needRuntime) {
                await ensureRuntimeDefaults();
            } else if (gid) {
                // ゲームファイルを読まない op（詳細表示等）は、_runtime 取得に失敗しても
                // projectFiles だけで応答できるようにする（エンジン未配信・一時的な断で全滅させない）
                try { await ensureRuntimeDefaults(); } catch { /* hydrateGameDefaults は existsFile ガード済み */ }
            }
            if (gid) await hydrateGame(gid, projectId);
            return await fn({ gameId: gid });
        } catch (e) {
            fnError = e;
            throw e;
        } finally {
            try {
                // fn が gameId を新設した場合（② パッケージ初期化の linkGame）にも previewFiles を flush できるよう再解決
                const after = projectId ? readJsonIfExists(`${projectDir(projectId)}/project.json`) : null;
                await flush(projectId, after?.gameId ?? gid);
            } catch (flushErr) {
                // fn の例外を flush の例外で差し替えない（原因が消える）。fn 成功時のみ flush 失敗を報告する
                if (!fnError) throw flushErrorToFriendly(flushErr);
                console.error('[backend] flush に失敗しました（処理本体のエラーを優先して報告します）:', flushErr);
            } finally {
                vfs.resetLayer();
            }
        }
    });
}

// ── ジョブ管理（server.mjs createJob 互換）─────────────
const jobs = new Map();
const jobAbort = new Map(); // id -> AbortController（job は JSON 化して UI へ返すため signal は別管理）
const MAX_JOBS = 50;
const PAID_API_JOB_KINDS = new Set([
    'story-scenario', 'story-analyze', 'story-image-prompts', 'story-image-generate',
    'story-model-generate', 'story-world-design', 'story-generate-json',
    'story-start-screen',
]);
function createJob(kind, gameId, runner) {
    const id = crypto.randomUUID();
    const ac = new AbortController();
    jobAbort.set(id, ac);
    const job = { id, kind, gameId, status: 'running', log: [], result: null, error: null, canceled: false, startedAt: new Date().toISOString(), finishedAt: null };
    jobs.set(id, job);
    if (jobs.size > MAX_JOBS) {
        const oldest = [...jobs.values()].filter((j) => j.status !== 'running')[0];
        if (oldest) { jobs.delete(oldest.id); jobAbort.delete(oldest.id); }
    }
    const onLog = (line) => {
        job.log.push(line);
        // 有料 API のログは永続保存対象なので切り詰めない。
        if (!PAID_API_JOB_KINDS.has(kind) && job.log.length > 2000) job.log.splice(0, job.log.length - 2000);
    };
    // ジョブ自身の usage.cost 行だけから集計し、別プロジェクトの並行処理を混入させない。
    const jobStartMs = Date.now();
    Promise.resolve()
        .then(() => runner(onLog, ac.signal))
        .then((result) => {
            job.result = result;
            job.status = result && result.ok === false ? 'failed' : 'done';
        })
        .catch((err) => {
            // 中断（AbortError）は失敗ではなく canceled として扱う（runner が安全な区切りで throwIfAborted した結果）。
            if (err && err.name === 'AbortError') {
                job.status = 'canceled';
                job.canceled = true;
                onLog('■ 処理を中断しました（安全な区切りで停止しました）');
            } else {
                job.status = 'failed';
                job.error = err.message;
            }
        })
        .finally(() => {
            const costLine = formatOpenRouterJobCostLine(job.log, { elapsedSec: (Date.now() - jobStartMs) / 1000 });
            if (costLine) onLog(costLine);
            job.costSummary = summarizeJobCost(kind, job.result, job.log);
            job.finishedAt = new Date().toISOString();
            jobAbort.delete(id);
        });
    return job;
}

/** 実行中ジョブへ中断要求（AbortController.abort）。runner が次の安全な区切りで throwIfAborted して止まる。 */
function requestCancelJob(id) {
    const job = jobs.get(id);
    if (!job) return { ok: false, error: 'job not found' };
    const ac = jobAbort.get(id);
    if (job.status === 'running' && ac) ac.abort();
    return { ok: true };
}

// ── ② ベースデータ生成のブラウザ版（共有ランタイム方式の初期化）─────────
// エンジンはコピーせず、ゲーム固有の _shared 上書き + placeholder スタートワールドを previewFiles へ生成する。
async function initPreviewPackage(projectId, onLog, signal = null) {
    const options = buildScaffoldOptions(projectId, { npmCi: false });
    const gid = options.gameId;
    const startMapId = options.startMapId;
    const dir = gameDir(gid);
    // ② はゲーム未 link のため withProjectVfs では未ハイドレート。gameId 確定後にここで用意する
    // （既定 _shared マスタ + schemas + dummy package.json + 既存 previewFiles）。
    await hydrateGame(gid, projectId);
    // 中断チェックは書き込み開始前のみ。以降は同期の多ファイル書き込みで途中中断すると map-list が
    // 未コピーのワールドを指す等の不整合になるため、開始後は最後まで実行する（安全優先）。
    signal?.throwIfAborted();
    const dc = readJson(path.join(CONFIG_DIR, 'default-characters.json'));

    onLog(`── パッケージ初期化: ${gid}（開始ワールド ${startMapId}）`);
    // ② 再実行時の旧キャラ ID（前回パッケージの残骸）をロケールから掃除するため、上書き前に控える
    const prevChars = readJsonIfExists(`${dir}/maps/_shared/characters.json`);
    const prevIds = (prevChars?.characters ?? []).map((c) => c.id);
    writeJson(`${dir}/maps/_shared/characters.json`, buildCharactersJson(options, dc));
    onLog('   maps/_shared/characters.json: 2 キャラ構成を生成');
    // ④/⑤再実行によるパッケージ再構築では③（GLB生成）は無効化されない。characters.json を
    // 既定モデルで作り直した直後に、生成済みプレイアブルGLBのパスを index の記録から復元する
    //（復元しないとプレビューが共通の少年/少女モデルへ戻る 2026-07-13 実障害）
    const restoredModels = reapplyPlayableGlbPaths(projectId, gid);
    if (restoredModels > 0) onLog(`   maps/_shared/characters.json: 生成済みプレイアブルGLB ${restoredModels}体分を復元`);

    // スキルは常に純正マスタ（_runtime 既定）から再構築する。previewFiles のパッチ適用済み
    // skills.json に再パッチすると元キャラ（kai/lilia）キーが無く、②再実行で全キャラのスキルが
    // 空配列になる（skills.json は ② 以外で変更されないため、マスタ起点で常に安全）
    const skills = readJson('/vfs/_runtime_defaults/maps/_shared/skills.json');
    for (const w of patchSkillsCharacterSkills(skills, options, dc)) onLog(`   ⚠ ${w}`);
    writeJson(`${dir}/maps/_shared/skills.json`, skills);
    onLog('   maps/_shared/skills.json: characterSkills を付け替え');

    for (const lang of ['ja', 'en']) {
        const p = `${dir}/maps/_shared/i18n/locales/${lang}.json`;
        const locale = readJson(p);
        // ⑤で取り込んだ i18n キーは保全しつつ、前回パッケージのキャラ ID 残骸だけ追加削除する
        patchCharacterLocale(locale, options, dc, lang, prevIds);
        writeJson(p, locale);
    }
    onLog('   locales(ja/en): タイトル・キャラ名を反映（旧キャラ分は削除）');

    writeJson(`${dir}/maps/_shared/map-list.json`, { maps: [startMapId] });

    // map-list に無い旧ワールド（startMapId 変更前の placeholder・取り込みデータ）を削除する。
    // 残すと参照されないまま IndexedDB の容量を圧迫し続ける
    for (const name of vfs.readdir(`${dir}/maps`)) {
        if (name === '_shared' || name === startMapId) continue;
        vfs.rm(`${dir}/maps/${name}`, true);
        onLog(`   maps/${name}/: map-list に無いため削除（開始ワールドは ${startMapId}）`);
    }

    // placeholder スタートワールド: _runtime の regalus を複製し mapId/meta.id を書換（treasures は除外）。
    // 既にスタートワールドが存在する（②を再実行・⑤取り込み済み等）場合は上書きしない＝取り込んだワールドを保全する。
    if (vfs.exists(`${dir}/maps/${startMapId}/meta.json`)) {
        onLog(`   maps/${startMapId}/: 既存のワールドを保全（placeholder で上書きしません。作り直すにはプロジェクトを削除して再作成）`);
    } else {
        // 複製元: ①解析が物語に合わせて選んだマイワールド（6 種）。取得失敗・未指定は regalus へフォールバック
        let baseId = 'regalus';
        const requestedBase = typeof options.startWorldBase === 'string' && /^[a-z][a-z0-9_]*$/.test(options.startWorldBase)
            ? options.startWorldBase : null;
        if (requestedBase && requestedBase !== 'regalus') {
            try {
                await ensureRuntimeMap(requestedBase);
                if (vfs.readdir(`/vfs/_runtime_defaults/maps/${requestedBase}`).length > 0) baseId = requestedBase;
                else onLog(`   ⚠ 土台 ${requestedBase} を取得できなかったため regalus を使用します`);
            } catch {
                onLog(`   ⚠ 土台 ${requestedBase} の取得に失敗したため regalus を使用します`);
            }
        }
        const baseDir = `/vfs/_runtime_defaults/maps/${baseId}`;
        let copied = 0;
        for (const name of vfs.readdir(baseDir)) {
            if (name === 'treasures.json') continue; // placeholder では使わない（要再生成のため除外）
            const json = readJsonIfExists(`${baseDir}/${name}`);
            if (!json || typeof json !== 'object') continue;
            if (typeof json.mapId === 'string') json.mapId = startMapId;
            if (name === 'meta.json') {
                if (typeof json.id === 'string') json.id = startMapId;
                if (json.player && 'deathRespawnMapId' in json.player) delete json.player.deathRespawnMapId;
            }
            writeJson(`${dir}/maps/${startMapId}/${name}`, json);
            copied++;
        }
        onLog(`   maps/${startMapId}/: placeholder を生成（${copied}ファイル・${baseId} 複製）`);
    }

    // treasures は後工程で作品固有データを生成するため土台からコピーしないが、エンジンは
    // マップ起動時に必ず取得する。空の有効JSONを置き、②直後のプレビューで404を発生させない。
    const treasuresPath = `${dir}/maps/${startMapId}/treasures.json`;
    if (!vfs.existsFile(treasuresPath)) {
        writeJson(treasuresPath, buildEmptyTreasuresJson(startMapId));
        onLog(`   maps/${startMapId}/treasures.json: placeholder（空）を生成`);
    }

    linkGame(projectId, gid);                       // project.gameId / game.storyProjectId / step 記録
    // 開始画面素材（⑤ 開始画面設計）はサイドステージのため、⑥系の再実行によるパッケージ再構築では無効化されない。
    // プロジェクト保存済みの素材・調整値をプレビューへ再反映する（未生成なら安全なフォールバック状態を書く）
    try {
        const startScreen = applyStartScreenToGame(projectId, gid);
        if (startScreen.artMode) onLog('   開始画面素材: 生成済みアートを再反映（背景＋立ち絵）');
    } catch (e) {
        onLog(`   ⚠ 開始画面素材の再反映をスキップ: ${e.message}`);
    }
    updateProjectMeta(projectId, (m) => ({ ...m, previewRev: (m.previewRev ?? 0) + 1 }));
    onLog(`✔ パッケージ初期化完了: ${gid}`);
    return { ok: true, gameId: gid, options };
}

// ── 公開（Phase D-1: publish API 連携） ──────────────────────────

/** ゲームディレクトリ配下の全ファイルを {path(相対), bytes} で列挙する。 */
/** ゲームディレクトリ配下を実体つきで読む（公開パッケージ組み立て用）。 */
function readGameFileEntries(dirAbs) {
    return walkGameFiles(dirAbs).map(({ path: rel }) => ({ path: rel, data: vfs.read(`${dirAbs}/${rel}`) }));
}

function walkGameFiles(dirAbs) {
    const out = [];
    const walk = (rel) => {
        for (const name of vfs.readdir(rel ? `${dirAbs}/${rel}` : dirAbs)) {
            const childRel = rel ? `${rel}/${name}` : name;
            const abs = `${dirAbs}/${childRel}`;
            if (vfs.isDirectory(abs)) walk(childRel);
            else out.push({ path: childRel, bytes: contentByteLength(vfs.read(abs)) });
        }
    };
    walk('');
    return out;
}

/**
 * 公開パッケージへ必須設定を同梱する。
 * - runtime-config.json … 共有ランタイム既定へ公開用既定を適用した複製。
 *   プロジェクト側の値は使わず**必ず共有既定から作り直す**ため、古いゲームでも
 *   公開し直せば現行の TURN 既定（STUNのみ・§22.4-2）と
 *   訪問受け入れ既定（ゲーム連邦の shellOrigins 固定）が入る。
 * - cache-manifest.json … 無ければ最小構成を生成（配信時に game-files が版番等を注入）
 */
async function ensurePublishConfigFiles(gid, onLog = () => {}) {
    const dir = gameDir(gid);
    try {
        const res = await fetch('/_runtime/maps/_shared/runtime-config.json', { cache: 'no-cache' });
        if (res.ok) {
            vfs.write(`${dir}/maps/_shared/runtime-config.json`, buildPublishRuntimeConfig(await res.text()));
            onLog('   maps/_shared/runtime-config.json: 共有既定へ TURN 既定（STUNのみ）と訪問受け入れ既定を適用して同梱');
        } else if (!vfs.existsFile(`${dir}/maps/_shared/runtime-config.json`)) {
            onLog('   ⚠ runtime-config 既定を取得できませんでした（同梱なしで継続＝エンジン既定動作）');
        }
    } catch {
        onLog('   ⚠ runtime-config 既定の取得に失敗しました（同梱なしで継続＝エンジン既定動作）');
    }
    const cmPath = `${dir}/maps/_shared/cache-manifest.json`;
    if (!vfs.existsFile(cmPath)) {
        const chars = readJsonIfExists(`${dir}/maps/_shared/characters.json`);
        const ids = (chars?.characters ?? []).map((c) => c?.id).filter(Boolean);
        writeJson(cmPath, buildMinimalCacheManifest(gid, ids));
        onLog('   maps/_shared/cache-manifest.json: 最小構成を生成して同梱');
    }
}

/** 公開前の計画（対象ファイル・容量）。公開ボタンの画面表示に使う。 */
/**
 * 「公開したときに使う容量」（⑦ 限定公開・マイ公開ゲームの「容量」と同じ基準）。
 * 同じゲームなのに画面ごとに違う数字が出ないよう、対象の選定・パスの読み替えは公開処理と
 * 同じ toPackageEntries / collectPublishFiles を使う（作業データ＝物語・解析・元画像は含めない）。
 * ファイル本体は読まずにサイズだけ数える（一覧のために 100MB を読み込まない）。
 */
async function publishByteSize(projectId) {
    const gameId = (await getProjectMeta(projectId))?.gameId ?? null;
    const entries = toPackageEntries({
        gameFiles: await listFileSizes(STORE_PREVIEW_FILES, projectId),
        assets: gameId ? await listAssetSizes(projectId) : [],
        gameId,
    });
    const { files, totalBytes } = collectPublishFiles(entries);
    return { total: totalBytes, fileCount: files.length };
}

async function buildPublishPlan(projectId) {
    const meta = readProjectMeta(projectId);
    const gid = meta.gameId;
    if (!gid) return { ok: false, error: '公開には ②（ベースデータ生成）の完了が必要です' };
    await hydrateGame(gid, projectId);
    await ensurePublishConfigFiles(gid);
    const { files, skipped, totalBytes } = buildPublishPackage({
        gameFiles: readGameFileEntries(gameDir(gid)),
        assets: await getAssetsForProject(projectId),
        gameId: gid,
    });
    if (!files.some((f) => f.path.startsWith('maps/'))) {
        return { ok: false, error: '公開できるゲームデータがありません（②以降の工程を完了してください）' };
    }
    return {
        ok: true, gameId: gid, fileCount: files.length, totalBytes, skippedCount: skipped.length,
        modelCount: files.filter((f) => f.path.startsWith('cast/models/')).length,
    };
}

/** 公開の実行（init → R2 直接アップロード → finalize）。ジョブとして進捗を流す。 */
async function runPublish(projectId, body, onLog, signal) {
    const accessToken = String(body.accessToken ?? '');
    if (!accessToken) throw new Error('ログインが必要です（セッションが見つかりません。再ログインしてください）');
    const meta = readProjectMeta(projectId);
    const gid = meta.gameId;
    if (!gid) throw new Error('公開には ②（ベースデータ生成）の完了が必要です');
    onLog(`── 公開パッケージの構築: ${gid}`);
    await hydrateGame(gid, projectId);
    await ensurePublishConfigFiles(gid, onLog);
    const dir = gameDir(gid);
    // 生成アセット（Meshy の GLB）は IndexedDB 側にあるため、ここで cast/models/** として合流させる。
    // 同梱しないと公開ゲームが共有CDNへモデルを探しにいって 404 になる（2026-07-25 実障害）
    const assets = await getAssetsForProject(projectId);
    const { files, skipped, totalBytes, dataByPath } = buildPublishPackage({
        gameFiles: readGameFileEntries(dir), assets, gameId: gid,
    });
    if (skipped.length > 0) onLog(`   対象外 ${skipped.length}件をスキップ（maps/cast 以外・非対応拡張子）`);
    const modelCount = files.filter((f) => f.path.startsWith('cast/models/')).length;
    onLog(`   アップロード対象: ${files.length}ファイル / ${(totalBytes / 1024 / 1024).toFixed(2)}MB（うち生成3Dモデル ${modelCount}件）`);
    if (assets.length > 0 && modelCount === 0) onLog('   ⚠ 生成3Dモデルを同梱できませんでした（パスが想定と異なります）');

    const authHeaders = { authorization: `Bearer ${accessToken}`, 'content-type': 'application/json' };
    onLog('── 公開初期化（検証・クォータ・規約同意の確認）');
    const initRes = await fetch('/api/publish/init', {
        method: 'POST', headers: authHeaders, signal,
        body: JSON.stringify({
            gameId: gid, projectId, titleJa: body.titleJa, titleEn: body.titleEn ?? null,
            descriptionJa: body.descriptionJa ?? null, files, acceptedDocuments: body.acceptedDocuments ?? {},
        }),
    });
    const init = await initRes.json().catch(() => null);
    if (!initRes.ok || init?.ok !== true) throw new Error(init?.error ?? `公開初期化に失敗しました（HTTP ${initRes.status}）`);
    onLog(`   rev ${init.rev} として公開します`);

    const uploads = init.uploads ?? [];
    onLog(`── アップロード（${uploads.length}ファイル・並列4）`);
    let done = 0;
    let index = 0;
    const uploadOne = async (u) => {
        const data = dataByPath.get(u.path);
        if (data === undefined) throw new Error(`${u.path}: 公開パッケージに実体がありません`);
        for (let attempt = 1; ; attempt++) {
            const res = await fetch(u.url, { method: 'PUT', headers: u.headers, body: data, signal });
            if (res.ok) break;
            if (attempt >= 2) throw new Error(`${u.path}: アップロードに失敗しました（HTTP ${res.status}）`);
            onLog(`   ⚠ ${u.path}: HTTP ${res.status} → 再試行`);
        }
        done += 1;
        if (done % 10 === 0 || done === uploads.length) onLog(`   ${done}/${uploads.length} 完了`);
    };
    await Promise.all(Array.from({ length: 4 }, async () => {
        while (index < uploads.length) {
            const u = uploads[index++];
            await uploadOne(u);
        }
    }));

    onLog('── 公開確定（アップロード実体の照合 → 反映）');
    const finRes = await fetch('/api/publish/finalize', {
        method: 'POST', headers: authHeaders, signal,
        body: JSON.stringify({
            gameId: gid, rev: init.rev, projectId, titleJa: body.titleJa, titleEn: body.titleEn ?? null,
            descriptionJa: body.descriptionJa ?? null, files,
        }),
    });
    const fin = await finRes.json().catch(() => null);
    if (!finRes.ok || fin?.ok !== true) throw new Error(fin?.error ?? `公開確定に失敗しました（HTTP ${finRes.status}）`);
    onLog(`✅ 公開完了: ${fin.playUrl}（${fin.visibility === 'unlisted' ? '限定公開: URLを知っている人だけが遊べます' : fin.visibility}）`);
    return { playUrl: fin.playUrl, gameId: gid, rev: fin.rev, visibility: fin.visibility };
}

/** 再実行前に後工程を破棄し、取り込み済みプレビューは②直後のベース状態へ戻す。 */
async function preparePipelineRerun(projectId, stageId, onLog = () => {}, { includeStage = false } = {}) {
    const before = readProjectMeta(projectId);
    const result = invalidatePipelineFrom(projectId, stageId, { includeStage });
    // 初回実行では空の後工程を無効化するだけなので、②のベースを無駄に再構築しない。
    // 既存 gameId を破棄する①/②再実行、または⑤-3取り込み済みの場合だけプレビューを戻す。
    const shouldClearPreview = (result.invalidatedStages.includes('scaffold') && !!result.oldGameId)
        || (stageId === 'scaffold' && !!before.gameId)
        || (result.invalidatedStages.includes('import') && !!before.steps?.import);
    if (shouldClearPreview) await deleteAllFiles(STORE_PREVIEW_FILES, projectId);
    if (result.clearsAssets || (stageId === 'scaffold' && !!before.gameId)) {
        await deleteAssetsForProject(projectId);
    }
    if (shouldClearPreview && !result.invalidatedStages.includes('scaffold')) {
        const gameId = readProjectMeta(projectId).gameId;
        if (gameId) {
            vfs.rm(gameDir(gameId), true);
            await initPreviewPackage(projectId, onLog);
        }
    }
    if (result.invalidatedStages.length > 0) {
        onLog(`↺ 後工程を初期化: ${result.invalidatedStages.join(' → ')}`);
    }
    return result;
}

// ── ルーティング（server.mjs handleApi 互換）────────────
// 未対応機能のエラー文言に内部構成・別バージョンの存在を書かない（公開版の絶対厳守事項）
const NOT_IN_BROWSER = (what) => ({ ok: false, error: `「${what}」は現在準備中のため、まだ利用できません。` });

// ── プロジェクトのエクスポート／インポート（.zip 1ファイル）─────────────
// 目的: ローカルPCへの保存（バックアップ）、環境をまたいだ持ち込み、似た別ゲームの土台としての複製。
// 生成物一式（物語・解析・プロンプト・ゲーム本体・生成画像・生成GLB）を含めるため、
// 取り込み側で LLM / Meshy を再実行せずにそのまま続きから作業・公開できる。

/** inspect した内容を import で読み直さないための短期キャッシュ（同一タブ内・直近1件）。 */
let inspectedArchive = null;

const textOf = (bytes) => new TextDecoder().decode(bytes);

async function loadArchiveBytes(bytes) {
    const files = await readZip(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
    const manifestBytes = files.get(MANIFEST_NAME);
    if (!manifestBytes) throw new ArchiveError('VoxelNovels のプロジェクトファイルではありません（manifest.json がありません）。');
    return { manifest: parseManifest(textOf(manifestBytes)), files };
}

/** 既存の projectId / gameId（取り込み時の衝突判定に使う）。 */
async function loadExistingIds() {
    const metas = await listProjectMetas();
    const gameIdOwners = {};
    for (const meta of metas) if (meta.gameId) gameIdOwners[meta.gameId] = meta.projectId;
    return { projectIds: metas.map((m) => m.projectId), gameIds: Object.keys(gameIdOwners), gameIdOwners };
}

const reservedGameIds = () => {
    const cfg = readJsonIfExists(path.join(CONFIG_DIR, 'reserved-game-ids.json')) ?? {};
    return [...(cfg.reserved ?? []), ...(cfg.reservedLanguageCodes ?? [])];
};

async function exportProjectArchive(projectId) {
    const meta = (await listProjectMetas()).find((m) => m.projectId === projectId);
    if (!meta) return { ok: false, error: '対象のプロジェクトが見つかりません。' };
    const [projectFiles, previewFiles, assetFiles] = await Promise.all([
        getAllFiles(STORE_PROJECT_FILES, projectId),
        getAllFiles(STORE_PREVIEW_FILES, projectId),
        getAssetsForProject(projectId),
    ]);
    const { manifest, entries } = buildArchive({ meta, projectFiles, previewFiles, assetFiles });
    const { parts, size } = await createZip(entries);
    return { ok: true, fileName: archiveFileName(manifest), parts, size, summary: summarizeArchive(manifest) };
}

async function inspectProjectArchive(bytes) {
    const loaded = await loadArchiveBytes(bytes);
    inspectedArchive = { token: crypto.randomUUID(), ...loaded };
    const existing = await loadExistingIds();
    const oldGameId = loaded.manifest.project.gameId;
    return {
        ok: true,
        token: inspectedArchive.token,
        summary: summarizeArchive(loaded.manifest),
        existing: { projectIds: existing.projectIds, gameIds: existing.gameIds },
        conflicts: {
            sameProject: existing.projectIds.includes(loaded.manifest.project.projectId),
            gameIdOwner: oldGameId ? (existing.gameIdOwners[oldGameId] ?? null) : null,
        },
        suggestedGameId: oldGameId ? suggestCloneGameId(oldGameId, existing.gameIds) : null,
    };
}

async function importProjectArchive(body) {
    const loaded = (body.token && inspectedArchive?.token === body.token)
        ? inspectedArchive
        : await loadArchiveBytes(body.bytes);
    const { manifest, files } = loaded;
    const existing = await loadExistingIds();
    const plan = planImport(manifest, {
        mode: body.mode, newGameId: body.newGameId, newTitle: body.newTitle,
        existing, reserved: reservedGameIds(),
    });

    // 復元で上書きするときは、旧ファイルが取り残されないよう先に消す
    if (plan.mode === 'restore' && plan.overwrite) {
        await deleteAllFiles(STORE_PROJECT_FILES, plan.projectId);
        await deleteAllFiles(STORE_PREVIEW_FILES, plan.projectId);
        await deleteAssetsForProject(plan.projectId);
    }

    const buckets = { project: [], preview: [], assets: [] };
    for (const file of manifest.files) {
        const raw = files.get(entryName(file.area, file.path));
        if (!raw) throw new ArchiveError(`ファイルが欠けています: ${file.path}`);
        const data = file.encoding === 'utf8' ? textOf(raw) : raw;
        const next = rewriteEntryForPlan({ area: file.area, path: file.path, data }, plan);
        buckets[next.area].push({ path: next.path, data: next.data });
    }
    try {
        await applyFileChanges(STORE_PROJECT_FILES, plan.projectId, buckets.project, []);
        await applyFileChanges(STORE_PREVIEW_FILES, plan.projectId, buckets.preview, []);
        await applyAssetChanges(plan.projectId, buckets.assets, []);
        await putProjectMeta(buildImportedMeta(manifest, plan));
    } catch (e) {
        throw flushErrorToFriendly(e);
    }
    inspectedArchive = null;
    return {
        ok: true, mode: plan.mode, projectId: plan.projectId, gameId: plan.gameId,
        overwritten: plan.overwrite, renamedFrom: plan.rename?.oldGameId ?? null,
        counts: { project: buckets.project.length, preview: buckets.preview.length, assets: buckets.assets.length },
    };
}

async function dispatch(method, pathname, body = {}) {
    ensureSeeded();
    const parts = pathname.split('/').filter(Boolean); // ['api', ...]
    if (parts[0] !== 'api') return { ok: false, error: 'not found' };

    // GET /api/jobs/:id
    if (parts.length === 3 && parts[1] === 'jobs' && method === 'GET') {
        const job = jobs.get(parts[2]);
        return job ? { ok: true, job } : { ok: false, error: 'job not found' };
    }
    // POST /api/jobs/:id/cancel（実行中ジョブへ中断要求）
    if (parts.length === 4 && parts[1] === 'jobs' && parts[3] === 'cancel' && method === 'POST') {
        return requestCancelJob(parts[2]);
    }
    // ── 公開（Phase D-1）: 事前計画と実行。実行は Supabase セッション（accessToken）必須 ──
    if (parts.length === 2 && parts[1] === 'publish-plan' && method === 'POST') {
        try {
            const projectId = String(body.projectId ?? '');
            return await withProjectVfs(projectId, () => buildPublishPlan(projectId));
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    if (parts.length === 2 && parts[1] === 'publish-run' && method === 'POST') {
        const projectId = String(body.projectId ?? '');
        return {
            ok: true,
            jobId: createJob('publish', null, (onLog, signal) => withProjectVfs(projectId, () => runPublish(projectId, body, onLog, signal))).id,
        };
    }
    // ── プロジェクトのエクスポート／インポート ──
    if (parts.length === 3 && parts[1] === 'project-archive' && method === 'POST') {
        const action = parts[2];
        try {
            ensureSeeded();
            if (action === 'export') return await runExclusive(() => exportProjectArchive(String(body.projectId ?? '')));
            if (action === 'inspect') return await inspectProjectArchive(body.bytes);
            if (action === 'import') return await runExclusive(() => importProjectArchive(body));
            return { ok: false, error: 'not found' };
        } catch (e) {
            return { ok: false, error: e?.message ?? String(e) };
        }
    }
    // GET /api/games（ブラウザ版はゲーム一覧を projects 由来で持つ＝手動ゲームは無い）
    if (parts.length === 2 && parts[1] === 'games' && method === 'GET') {
        return { ok: true, games: await listBrowserGames() };
    }
    // POST /api/prompt
    if (parts.length === 2 && parts[1] === 'prompt' && method === 'POST') {
        ensureSeeded();
        return { ok: true, ...buildAiRequest(body) };
    }
    // GET /api/llm-status
    if (parts.length === 2 && parts[1] === 'llm-status' && method === 'GET') {
        return { ok: true, status: llmStatus(), health: await llmHealth() };
    }
    // GET /api/story-scenario（画面が使う入力上限・編集欄上限。値は config を正とし js へ書かない）
    if (parts.length === 2 && parts[1] === 'story-scenario' && method === 'GET') {
        return { ok: true, limits: scenarioUiLimits() };
    }
    // POST /api/story-scenario/estimate { text }（実行前に見せる解析コストの予測）
    if (parts.length === 3 && parts[1] === 'story-scenario' && parts[2] === 'estimate' && method === 'POST') {
        return { ok: true, ...(await scenarioCostPreview(body.text)) };
    }
    // POST /api/story-scenario { text, fileName }（📖物語から新規ゲーム作成の第1段）
    // 長編小説 → ゲームシナリオ文書。プロジェクト作成前に走るためプロジェクト非依存のジョブにする
    // （IndexedDB へは保存せず、生成結果を画面の編集欄へ返すだけ＝VFS も使わない）
    if (parts.length === 2 && parts[1] === 'story-scenario' && method === 'POST') {
        // config（story-pipeline / llm）だけを読む工程なので VFS のプロジェクト層は用意しない
        //（dispatch 冒頭の ensureSeeded で config は読める）
        return {
            ok: true,
            jobId: createJob('story-scenario', null, (onLog, signal) => runScenarioDraft({
                text: body.text, fileName: body.fileName ?? null, onLog, signal,
            })).id,
        };
    }
    // GET /api/image-models（config/llm.json の image から。画像生成自体は Phase C では UI 非表示）
    if (parts.length === 2 && parts[1] === 'image-models' && method === 'GET') {
        const img = llmConfig().image ?? {};
        const settings = openRouterModelSettings();
        const models = effectiveImageModelCatalog();
        const n = img.nsfw ?? {};
        return {
            ok: true, defaultProvider: 'openrouter', defaultModel: settings.activeImageModel,
            groups: models.length ? [{ provider: 'openrouter', label: 'OpenRouter（$ 直接課金・あなたのキー）', models }] : [],
            models,
            nsfw: { recommendedModel: settings.effective.imageAdult ?? n.recommendedModel ?? null, keywords: Array.isArray(n.keywords) ? n.keywords : [] },
            generationCostGuide: { meshy: meshyConfig().creditGuide ?? {} },
        };
    }
    // GET /api/settings/ui（config/ui.json はビルド時にVFSへ同梱される）
    if (parts.length === 3 && parts[1] === 'settings' && parts[2] === 'ui' && method === 'GET') {
        return { ok: true, processing: uiConfig().processing ?? {} };
    }
    // GET /api/settings/models（localStorage 由来の保存値は runtime-keys shim が読む）
    if (parts.length === 3 && parts[1] === 'settings' && parts[2] === 'models' && method === 'GET') {
        return { ok: true, ...openRouterModelSettings() };
    }
    // POST /api/settings/model-pricing（OpenRouter 公式APIから無丸めの単価文字列を取得）
    if (parts.length === 3 && parts[1] === 'settings' && parts[2] === 'model-pricing' && method === 'POST') {
        try {
            return { ok: true, ...await openRouterModelPricing(body.model, { key: body.key }) };
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // POST /api/settings/test（接続テスト。キーは localStorage 由来を runtime-keys shim が読む）
    if (parts.length === 3 && parts[1] === 'settings' && parts[2] === 'test' && method === 'POST') {
        if (body.service === 'openrouter') {
            const h = await llmHealth({ key: body.key });
            return { ok: h.reachable, ...h };
        }
        if (body.service === 'meshy') {
            // api.meshy.ai は CORS を許可しているためブラウザから直接疎通確認できる（server.mjs と同一ロジック）
            const typedKey = typeof body.key === 'string' && body.key.trim() !== '' ? body.key.trim() : null;
            const { key: storedKey } = resolveMeshyKey();
            const key = typedKey ?? storedKey;
            if (!key) return { ok: false, error: 'Meshy キーが未設定です。API設定画面で登録してください' };
            try {
                const r = await fetch(`${meshyConfig().baseUrl}/openapi/v1/image-to-3d?pageSize=1`, {
                    headers: { authorization: `Bearer ${key}` },
                    signal: AbortSignal.timeout(15000),
                });
                if (!r.ok) {
                    const d = await r.json().catch(() => null);
                    return { ok: false, error: `キーが無効です (HTTP ${r.status}): ${d?.message ?? ''}` };
                }
                return { ok: true, message: '疎通 OK（タスク一覧 API に接続できました）' };
            } catch (e) {
                return { ok: false, error: `Meshy へ接続できません: ${e.message}` };
            }
        }
        return { ok: false, error: 'service は openrouter か meshy を指定してください' };
    }
    if (parts.length === 3 && parts[1] === 'settings' && parts[2] === 'clear' && method === 'POST') {
        return { ok: true };
    }
    // POST /api/asset-fetch { path }（GLB ビューア用・読み取り専用。IndexedDB の assetFiles を直接返す。
    // dispatch は同一プロセス呼び出しのためバイナリをそのまま返せる＝base64 変換不要）
    if (parts.length === 2 && parts[1] === 'asset-fetch' && method === 'POST') {
        const rel = String(body.path ?? '').replace(/^assets\//, '');
        if (rel.includes('..') || !/^[A-Za-z0-9._/-]+\.glb$/.test(rel)) return { ok: false, error: '不正なファイル名です' };
        const data = await getAssetFile(rel);
        if (data === undefined) return { ok: false, error: 'GLB が見つかりません' };
        return { ok: true, data, mime: 'model/gltf-binary' };
    }
    // POST /api/asset-exists { paths: string[] }（③キャストカードの推奨GLB「未作成」表示用・読み取り専用）
    if (parts.length === 2 && parts[1] === 'asset-exists' && method === 'POST') {
        const exists = {};
        for (const raw of (Array.isArray(body.paths) ? body.paths : []).slice(0, 200)) {
            const key = String(raw);
            const rel = key.replace(/^assets\//, '');
            exists[key] = !rel.includes('..') && /^[A-Za-z0-9._/-]+\.glb$/i.test(rel) && await hasAssetFile(rel);
        }
        return { ok: true, exists };
    }
    // /api/projects...
    if (parts[1] === 'projects') return dispatchProjects(method, parts, body);
    // /api/games/:id/...（ブラウザ版はプレビュー導線のみ。検証・ビルド等はローカル版）
    if (parts[1] === 'games' && parts.length >= 4) return NOT_IN_BROWSER(`ゲーム操作 ${parts[3]}`);

    return { ok: false, error: `不明な API: ${method} ${pathname}` };
}

async function dispatchProjects(method, parts, body) {
    // GET /api/projects
    if (parts.length === 2 && method === 'GET') {
        const metas = await listProjectMetas();
        const projects = (await Promise.all(metas.map(async (m) => ({
            projectId: m.projectId,
            createdAt: m.createdAt ?? null,
            novel: m.novel ?? null,
            gameId: m.gameId ?? null,
            title: m.title ?? null,
            steps: Object.fromEntries(STORY_STEPS.map(({ id }) => [id, m.steps?.[id] ? { at: m.steps[id].at, ok: m.steps[id].ok !== false } : null])),
            // ホームの「未生成ユニット」判定とプレビュー可否は実生成状態（generated/status.json）を見る。
            // Node 版 listProjects と同じくここで返す（欠けると全ユニットが未生成に見え、
            // 完了済みの案件に「⑥-2 JSON 生成（未生成 N 件）」が出続けてプレビューも消える）
            generated: parseStoredJson(await getFile(STORE_PROJECT_FILES, m.projectId, 'generated/status.json')),
            usage: m.usage ?? null,
        })))).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
        ensureSeeded();
        return { ok: true, projects, units: listGenerateUnits() };
    }
    // POST /api/projects（新規作成）
    if (parts.length === 2 && method === 'POST') {
        return runExclusive(async () => {
            ensureSeeded();
            vfs.resetLayer();
            const metas = await listProjectMetas();
            for (const m of metas) vfs.hydrate(`${projectDir(m.projectId)}/project.json`, JSON.stringify(m)); // dup 判定用
            try {
                const project = createProject({ text: body.text, fileName: body.fileName });
                await flush(project.projectId, null);
                return { ok: true, project };
            } catch (e) {
                return { ok: false, error: e.message };
            } finally {
                vfs.resetLayer();
            }
        });
    }
    const projectId = parts[2];
    // GET /api/projects/:id
    if (parts.length === 3 && method === 'GET') {
        return withProjectVfs(projectId, () => {
            const detail = getProjectDetail(projectId);
            let startScreen = null;
            try { startScreen = readStartScreenState(projectId); } catch { /* 解析前は未提供 */ }
            return { ok: true, units: listGenerateUnits(), ...detail, startScreen };
        });
    }
    const action = parts[3];
    // GET /api/projects/:id/size … ホームの「ゲーム」欄に出す容量。
    // 一覧応答へ含めると全プロジェクトのバイナリを読むことになるため、別 API にして
    // 画面描画のあとから取りにいく（描画をブロックしない）。
    if (parts.length === 4 && action === 'size' && method === 'GET') {
        return { ok: true, bytes: await publishByteSize(projectId) };
    }
    // 生成ログ履歴（Node 版の generation-logs.json と同じデータを IndexedDB へ保存）。
    if (parts.length === 4 && action === 'generation-logs' && method === 'GET') {
        return withProjectVfs(projectId, () => ({ ok: true, ...readGenerationLogs(projectId) }));
    }
    if (parts.length === 4 && action === 'generation-logs' && method === 'POST') {
        return withProjectVfs(projectId, () => ({ ok: true, run: saveGenerationLog(projectId, body.run) }));
    }
    if (parts.length === 4 && action === 'novel' && method === 'PUT') {
        if (typeof body.text !== 'string' || body.text.trim().length < 200) {
            return { ok: false, error: '物語の本文が短すぎます（200文字以上のテキストファイルが必要です）。' };
        }
        return withProjectVfs(projectId, async () => {
            await preparePipelineRerun(projectId, 'analyze', () => {}, { includeStage: true });
            const meta = replaceProjectNovel(projectId, { text: body.text, fileName: body.fileName });
            return { ok: true, meta };
        });
    }
    // GET /api/projects/:id/image-prompts/:file
    if (parts.length === 5 && action === 'image-prompts' && method === 'GET') {
        return withProjectVfs(projectId, () => ({ ok: true, content: readImagePromptFile(projectId, decodeURIComponent(parts[4])) }));
    }
    // GET/PUT /api/projects/:id/generated-json/:unitId（⑤-2生成JSONの手動編集）
    if (parts.length === 5 && action === 'generated-json' && method === 'GET') {
        try {
            return await withProjectVfs(projectId, () => ({ ok: true, ...readGeneratedUnitJson(projectId, decodeURIComponent(parts[4])) }));
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    if (parts.length === 5 && action === 'generated-json' && method === 'PUT') {
        try {
            return await withProjectVfs(projectId, async () => {
                const saved = saveGeneratedUnitJson(projectId, decodeURIComponent(parts[4]), body.text);
                await preparePipelineRerun(projectId, 'generateJson');
                return { ok: true, ...saved };
            }, { needRuntime: true });
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // GET /api/projects/:id/cast-image/:file（IndexedDB 上の画像を base64 で返す。
    // <img src> の実 HTTP は使えないため、UI 側が blob URL に変換して表示する）
    if (parts.length === 5 && action === 'cast-image' && method === 'GET') {
        const file = decodeURIComponent(parts[4]).split('?')[0];
        if (!/^[A-Za-z0-9._-]+\.(png|jpg|jpeg|webp)$/i.test(file)) return { ok: false, error: '不正なファイル名です' };
        return withProjectVfs(projectId, () => {
            const abs = path.join(projectPaths(projectId).imagesDir, file);
            if (!vfs.existsFile(abs)) return { ok: false, error: '画像が見つかりません' };
            const data = vfs.read(abs);
            const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data.buffer ?? data, data.byteOffset ?? 0, data.byteLength ?? data.length);
            let bin = '';
            for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
            const ext = file.split('.').pop().toLowerCase();
            const mime = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg';
            return { ok: true, dataBase64: btoa(bin), mime };
        });
    }
    // POST /api/projects/:id/cast-image/:castId（「画像変更」= 外部画像への差し替え。base64 JSON）
    if (parts.length === 5 && action === 'cast-image' && method === 'POST') {
        try {
            return await withProjectVfs(projectId, async () => {
                await preparePipelineRerun(projectId, 'imagePrompts');
                return saveUploadedImage(projectId, decodeURIComponent(parts[4]), {
                    dataBase64: body.dataBase64, fileName: body.fileName,
                });
            }, { needRuntime: true });
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // GET /api/projects/:id/start-screen-image/:file（⑤ 開始画面設計素材のプレビュー。base64 で返し UI が blob URL 化）
    if (parts.length === 5 && action === 'start-screen-image' && method === 'GET') {
        const file = decodeURIComponent(parts[4]).split('?')[0];
        if (!/^[A-Za-z0-9._-]+\.(png|jpg|jpeg|webp)$/i.test(file)) return { ok: false, error: '不正なファイル名です' };
        return withProjectVfs(projectId, () => {
            const abs = path.join(projectPaths(projectId).startScreenImagesDir, file);
            if (!vfs.existsFile(abs)) return { ok: false, error: '画像が見つかりません' };
            const data = vfs.read(abs);
            const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data.buffer ?? data, data.byteOffset ?? 0, data.byteLength ?? data.length);
            let bin = '';
            for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
            const ext = file.split('.').pop().toLowerCase();
            const mime = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg';
            return { ok: true, dataBase64: btoa(bin), mime };
        });
    }
    // POST /api/projects/:id/start-screen-image/:slot（自作画像への差し替え。slot = 'background' | castId）
    if (parts.length === 5 && action === 'start-screen-image' && method === 'POST') {
        try {
            return await withProjectVfs(projectId, () => saveStartScreenUpload(projectId, decodeURIComponent(parts[4]), {
                dataBase64: body.dataBase64, fileName: body.fileName,
            }));
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // POST /api/projects/:id/start-screen/refine/:castId（保存済み立ち絵の足元補正。無償・API課金なし）
    if (parts.length === 6 && action === 'start-screen' && parts[4] === 'refine' && method === 'POST') {
        try {
            return await withProjectVfs(projectId, () => refineStartScreenPortrait(projectId, decodeURIComponent(parts[5])));
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // POST /api/projects/:id/start-screen/generate（⑤ 背景・立ち絵の自動生成。ジョブ化）
    if (parts.length === 5 && action === 'start-screen' && parts[4] === 'generate' && method === 'POST') {
        const genCastIds = Array.isArray(body.castIds) && body.castIds.length > 0 ? body.castIds : null;
        const genModel = typeof body.model === 'string' && body.model.trim() !== '' ? body.model.trim() : null;
        return {
            ok: true,
            jobId: createJob(START_SCREEN_JOB_KIND, projectId, (onLog, signal) => withProjectVfs(projectId, () => runStartScreenGenerate(projectId, {
                background: body.background === true ? true : null,
                castIds: genCastIds,
                model: genModel,
                batch: body.batch === true,
                onLog,
                signal,
            }))).id,
        };
    }
    // PUT /api/projects/:id/start-screen（配置・見た目の調整値を保存してゲームへ再適用）
    if (parts.length === 4 && action === 'start-screen' && method === 'PUT') {
        try {
            return await withProjectVfs(projectId, () => saveStartScreenSettings(projectId, body));
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }
    // POST /api/projects/:id/cast-glb/:castId/:slot（役割別 GLB を個別差し替え）
    if (parts.length === 6 && action === 'cast-glb' && method === 'POST') {
        try {
            return await withProjectVfs(projectId, async () => {
                await preparePipelineRerun(projectId, 'imagePrompts');
                return saveUploadedGlb(
                    projectId,
                    decodeURIComponent(parts[4]),
                    decodeURIComponent(parts[5]),
                    { dataBase64: body.dataBase64, fileName: body.fileName },
                );
            }, { needRuntime: true });
        } catch (e) {
            return { ok: false, error: e.message };
        }
    }

    switch (`${method} ${action}`) {
        case 'POST analyze':
            return { ok: true, jobId: createJob('story-analyze', projectId, (onLog, signal) => withProjectVfs(projectId, async () => {
                await preparePipelineRerun(projectId, 'analyze', onLog);
                return runAnalyze(projectId, { onLog, signal });
            })).id };
        case 'PUT analysis':
            return withProjectVfs(projectId, async () => {
                const settingsOnly = body.settingsOnly === true;
                const input = settingsOnly
                    ? mergeEditableGameSettings(readAnalysis(projectId), body.analysis ?? {})
                    : (body.analysis ?? {});
                if (!settingsOnly) await preparePipelineRerun(projectId, 'analyze');
                const source = inspectNovelSource(readNovel(projectId));
                const requestedUrl = input?.game?.originalUrl;
                saveAnalysis(projectId, normalizeAnalysis(input, {
                    ...source,
                    originalUrl: resolveDocumentOriginalUrl(requestedUrl, source.originalUrls, source.generationLanguage),
                }));
                // 設定案の編集は軽微な手直し用のため、後工程データは一切初期化せず、
                // 作成済みのロケール・game-info へ編集内容だけを直接反映する。
                // プレビュのシェル（タイトル・原作リンク）は preview-sw が analysis.json から
                // 直接描画するため、保存だけで追従する。
                if (settingsOnly) applyEditedGameSettings(projectId);
                return {
                    ok: true,
                    analysis: readJsonIfExists(projectPaths(projectId).analysis),
                    resetFrom: settingsOnly ? null : 'analyze',
                };
            });
        case 'POST image-prompts': {
            const castIds = Array.isArray(body.castIds) && body.castIds.length ? body.castIds : null;
            return { ok: true, jobId: createJob('story-image-prompts', projectId, (onLog, signal) => withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'imagePrompts', onLog); return runImagePrompts(projectId, { castIds, onLog, signal }); }, { needRuntime: true })).id };
        }
        case 'POST world-design':
            return { ok: true, jobId: createJob('story-world-design', projectId, (onLog, signal) => withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'worldDesign', onLog); return runWorldDesign(projectId, { onLog, signal }); }, { needRuntime: true })).id };
        case 'PUT world-design':
            return withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'worldDesign'); return { ok: true, design: saveWorldDesign(projectId, body.design) }; }, { needRuntime: true });
        case 'POST request-text':
            return withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'requestText'); return { ok: true, ...buildRequestText(projectId, { force: !!body.force }) }; }, { needRuntime: true });
        case 'PUT request-text': {
            if (typeof body.text !== 'string' || body.text.trim() === '') return { ok: false, error: 'text（依頼文）が空です' };
            return withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'requestText'); saveRequestText(projectId, body.text); return { ok: true }; }, { needRuntime: true });
        }
        case 'POST scaffold': {
            // ② ベースデータ生成 = 共有ランタイム方式の初期化（旧UI向けdryRun APIは互換維持）
            if (body.dryRun) {
                return withProjectVfs(projectId, () => {
                    const options = buildScaffoldOptions(projectId, { npmCi: false });
                    return {
                        ok: true, options,
                        report: {
                            ok: true, opts: options, errors: [], warnings: [],
                            steps: [
                                `共有ランタイム方式でパッケージを初期化（gameId: ${options.gameId}）`,
                                `maps/_shared: characters / skills / locales(ja,en) / map-list を生成`,
                                `maps/${options.startMapId}/: placeholder（${options.startWorldBase ?? 'regalus'} 複製）を生成`,
                                'エンジンは /_runtime/ を共有（コピーなし）。npm ci/build は不要',
                            ],
                        },
                    };
                });
            }
            return { ok: true, jobId: createJob('story-scaffold', null, (onLog, signal) => withProjectVfs(projectId, async () => { await preparePipelineRerun(projectId, 'scaffold', onLog); return initPreviewPackage(projectId, onLog, signal); }, { needRuntime: true })).id };
        }
        case 'POST generate-json': {
            const units = Array.isArray(body.units) && body.units.length ? body.units : null;
            // checkpoint = ユニット境界での中間 flush。タブクラッシュ/リロードでも生成済みユニット
            //（支払い済み LLM 実費）を失わず、再実行は続きから再開できる（mutex 保持中のため安全）。
            // 容量超過などの失敗は withProjectVfs の最終 flush と同様に対処可能な文言へ変換する
            return {
                ok: true,
                jobId: createJob('story-generate-json', projectId, (onLog, signal) => withProjectVfs(
                    projectId,
                    async ({ gameId }) => {
                        await preparePipelineRerun(projectId, 'generateJson', onLog);
                        return runGenerateJson(projectId, {
                            units,
                            requireComplete: body.requireComplete === true,
                            onLog,
                            signal,
                            checkpoint: () => flush(projectId, gameId).catch((e) => { throw flushErrorToFriendly(e); }),
                        });
                    },
                    { needRuntime: true },
                )).id,
            };
        }
        case 'GET import-plan':
            return withProjectVfs(projectId, () => ({ ok: true, ...buildImportPlan(projectId) }), { needRuntime: true });
        case 'POST import':
            return {
                ok: true,
                jobId: createJob('story-import', projectId, (onLog, signal) => withProjectVfs(projectId, async ({ gameId }) => {
                    // 1ボタン内の安全ゲート。UIを経由しないAPI呼び出しでも、プランNGなら一切適用しない。
                    onLog('── 取り込み事前確認 ──');
                    const planInfo = buildImportPlan(projectId);
                    const planErrors = planInfo.plan.flatMap((item) => item.errors.map((error) => `${item.relPath}: ${error}`));
                    if (planInfo.plan.length === 0) throw new Error('取り込めるJSONがありません。⑥-2 JSON生成を先に実行してください。');
                    if (planErrors.length > 0) {
                        for (const error of planErrors) onLog(`  ✖ ${error}`);
                        throw new Error(`取り込み事前確認で ${planErrors.length}件の問題が見つかりました。ゲームデータは変更していません。`);
                    }
                    onLog(`✔ 取り込み対象 ${planInfo.plan.length}ファイル（事前検証OK）`);

                    const out = { import: runImport(projectId, { onLog }) };
                    signal?.throwIfAborted();

                    onLog('── generate:treasures（ブラウザVFS） ──');
                    generateTreasuresInVfs(gameDir(gameId));
                    const mapIds = readJson(`${gameDir(gameId)}/maps/_shared/map-list.json`).maps ?? [];
                    const treasureMaps = mapIds.map((mapId) => {
                        const json = readJsonIfExists(`${gameDir(gameId)}/maps/${mapId}/treasures.json`);
                        return { mapId, count: Array.isArray(json?.treasures) ? json.treasures.length : -1 };
                    });
                    out.treasures = { ok: treasureMaps.every((entry) => entry.count >= 0), maps: treasureMaps };
                    for (const entry of treasureMaps) onLog(`  ${entry.count >= 0 ? '✔' : '✖'} ${entry.mapId}: 宝箱 ${Math.max(0, entry.count)}件`);
                    signal?.throwIfAborted();

                    onLog('── 取り込み後の自動検証 ──');
                    out.schema = runValidateMapsInProcess(gameId, { onLine: onLog });
                    signal?.throwIfAborted();
                    out.integrity = runIntegrityCheck(gameId);
                    onLog(`integrity: ${out.integrity.ok ? 'OK' : 'NG'}（エラー ${out.integrity.errorCount} / 警告 ${out.integrity.warningCount}）`);
                    out.i18n = runI18nCheck(gameId);
                    onLog(`i18n: ${out.i18n.ok ? 'OK' : 'NG'}（ja不足 ${out.i18n.messagesMissingInJa.length} / en不足 ${out.i18n.messagesMissingInEn.length} / 未翻訳 ${out.i18n.rawStringsUntranslated.length}）`);
                    for (const line of formatI18nFindings(out.i18n)) onLog(line);
                    signal?.throwIfAborted();
                    out.assets = await runBrowserAssetCheck(gameId, { onLine: onLog, signal });

                    out.ok = out.import.results.every((r) => r.status !== 'rejected')
                        && out.treasures.ok && out.schema.ok && out.integrity.ok && out.i18n.ok && out.assets.ok;
                    recordStep(projectId, 'import', {
                        ok: out.ok,
                        written: out.import.results.filter((r) => ['created', 'overwritten', 'merged'].includes(r.status)).length,
                        rejected: out.import.results.filter((r) => r.status === 'rejected').length,
                        i18nMerged: out.import.i18nMerged,
                        validation: {
                            treasures: out.treasures.ok,
                            schema: out.schema.ok,
                            integrity: out.integrity.ok,
                            i18n: out.i18n.ok,
                            assets: out.assets.ok,
                        },
                    });
                    updateProjectMeta(projectId, (m) => ({ ...m, previewRev: (m.previewRev ?? 0) + 1 }));
                    onLog(out.ok
                        ? '✔ 取り込み・treasures生成・全検証が完了しました（プレビューで確認できます）'
                        : '✖ 取り込み後の検証に問題があります（上のログを確認してください）');
                    return out;
                }, { needRuntime: true })).id,
            };
        case 'POST delete':
            return runExclusive(async () => {
                await deleteAllFiles(STORE_PROJECT_FILES, projectId);
                await deleteAllFiles(STORE_PREVIEW_FILES, projectId);
                await deleteAssetsForProject(projectId);
                await deleteProjectMeta(projectId);
                return { ok: true, projectId, deletedDir: `projects/${projectId}/` };
            });
        case 'POST model-generate': {
            // 3D キャラ自動生成（Meshy）。api.meshy.ai は CORS 許可のためブラウザから直接実行できる。
            // checkpoint = 工程境界（taskId 記録直後・工程完了・キャスト完了）での flush。
            // 長時間ジョブ中のタブクラッシュでも課金済みタスクへ再アタッチでき、GLB も逐次確定する
            const modelCastIds = Array.isArray(body.castIds) && body.castIds.length > 0 ? body.castIds : null;
            const modelBatch = body.batch === true;
            return {
                ok: true,
                jobId: createJob('story-model-generate', projectId, (onLog, signal) => withProjectVfs(
                    projectId,
                    async ({ gameId }) => {
                        await preparePipelineRerun(projectId, 'imagePrompts', onLog);
                        return runModelGenerate(projectId, {
                        castIds: modelCastIds, batch: modelBatch, onLog, signal,
                        checkpoint: () => flush(projectId, gameId).catch((e) => { throw flushErrorToFriendly(e); }),
                    }); },
                    { needRuntime: true },
                )).id,
            };
        }
        case 'POST image-generate': {
            // ③ キャスト画像の自動生成（OpenRouter 直接・server.mjs と同一パラメータ。Phase C 段階②）
            const genCastIds = Array.isArray(body.castIds) && body.castIds.length > 0 ? body.castIds : null;
            const genModel = typeof body.model === 'string' && body.model.trim() !== '' ? body.model.trim() : null;
            const genProvider = body.provider === 'openrouter' || body.provider === 'workers-ai' ? body.provider : null;
            const genNsfwPolicy = ['grok', 'sanitize', 'asis'].includes(body.nsfwPolicy) ? body.nsfwPolicy : null;
            const imageBatch = body.batch === true;
            return {
                ok: true,
                jobId: createJob('story-image-generate', projectId, (onLog, signal) => withProjectVfs(
                    projectId,
                    async () => {
                        await preparePipelineRerun(projectId, 'imagePrompts', onLog);
                        return runImageGenerate(projectId, { castIds: genCastIds, provider: genProvider, model: genModel, nsfwPolicy: genNsfwPolicy, batch: imageBatch, onLog, signal });
                    },
                    { needRuntime: true },
                )).id,
            };
        }
        default:
            return { ok: false, error: `不明な API: ${method} /api/projects/${projectId}/${action ?? ''}` };
    }
}

export const backend = { dispatch };
